/**
 * Deleted companies tab (SF-4813): every company deletion, with Cancel and Retry.
 *
 * "In progress" lists scheduled, running, failed and stopped deletions (soonest
 * first); "History" lists finished and cancelled ones. Both tables are paginated
 * on the server. Needs admin:companies:delete; sf-api checks it again. Cancel
 * and Retry also need the company to be one the person may change (SF-4668):
 * other rows say so instead, and nothing is offered until that list is known.
 *
 * SF-5057: staff who also have admin:companies:delete:force can tick waiting deletions of
 * recently made Demo or Test companies (the row's `can_force`) and delete them now.
 *
 * While a deletion is running, or forced and waiting to start (on any page, from sf-api's
 * `in_flight` count), both tables are read again every 30 seconds so it moves to History
 * without reloading the page.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { List } from '@refinedev/antd';
import { useInvalidate, usePermissions } from '@refinedev/core';
import { useQueryClient } from '@tanstack/react-query';
import { Alert, Button, Result, Space, Table, Typography, message } from 'antd';
import type { TableRowSelection } from 'antd/es/table/interface';

import { hasPermission } from '../../utils/permissions';
import {
  COMPANY_DELETE_FORCE_PERMISSION,
  COMPANY_DELETE_PERMISSION,
  DELETIONS_PAGE_SIZE,
  DELETIONS_POLL_MS,
  cancelDeletionArgs,
  companyDeletionsQueryKey,
  deletionActionDoneMessage,
  deletionActionErrorMessage,
  deletionsInFlight,
  retryDeletionArgs,
  useCompanyDeletions,
  useDeletionAction,
  type CompanyDeletionRow,
  type CompanyDeletionsPage as DeletionsPageData,
  type CompanyDeletionsView,
} from './api/companyDeleteClient';
import { CompaniesTabs } from './components/CompaniesTabs';
import { activeDeletionColumns, historyDeletionColumns } from './components/deletionColumns';
import type { DeletionAction } from './components/DeletionActionButton';
import { ForceDeleteBar } from './components/ForceDeleteBar';
import { forceTickBlock, reconcileForceTicks, rowsSupportForce } from './deletionForce';
import {
  canEditCompanyId,
  settledEditScope,
  useEditableCompanies,
  type EditableCompanies,
} from './api/editableCompaniesClient';

const { Title, Text } = Typography;

const VIEW_TEXT: Record<CompanyDeletionsView, { title: string; empty: string; failed: string }> = {
  active: {
    title: 'In progress',
    empty: 'No deletions are scheduled or running.',
    failed: 'Could not load the deletions in progress.',
  },
  history: {
    title: 'History',
    empty: 'No deletions have finished or been cancelled yet.',
    failed: 'Could not load the deletion history.',
  },
};

interface DeletionsTableProps {
  view: CompanyDeletionsView;
  busyId?: string | null;
  onAction?: (row: CompanyDeletionRow, action: DeletionAction) => void;
  editScope?: EditableCompanies | null;
  /** SF-5057: force delete tick boxes; only when the person may force and the scope is known. */
  forceSelection?: ForceSelection;
  /** SF-5057: told each time this table's page loads, so ticks can follow the fresh rows. */
  onRowsLoaded?: (rows: CompanyDeletionRow[]) => void;
  /** Told each time this table's page loads, with the whole answer (counts included). */
  onPageLoaded?: (page: DeletionsPageData) => void;
  /**
   * SF-5057: told whether the rows on screen are a settled, successful read (false while the
   * first read or a re-read runs, or after it failed), so Delete now waits for fresh rows.
   */
  onReadyChange?: (ready: boolean) => void;
  /** Read the table again every this many ms (false: only on actions and focus). */
  refetchInterval?: number | false;
}

interface ForceSelection {
  keys: string[];
  onChange: (keys: React.Key[], rows: (CompanyDeletionRow | undefined)[]) => void;
  editScope: EditableCompanies;
}

/** Tick boxes for force delete, or none when this sf-api does not send `can_force`. */
function forceRowSelection(
  rows: CompanyDeletionRow[],
  selection: ForceSelection | undefined,
): TableRowSelection<CompanyDeletionRow> | undefined {
  if (!selection || !rowsSupportForce(rows)) return undefined;
  return {
    selectedRowKeys: selection.keys,
    onChange: selection.onChange,
    // Keep ticks on other pages when moving between pages.
    preserveSelectedRowKeys: true,
    getCheckboxProps: (row) => {
      const block = forceTickBlock(row, selection.editScope);
      return {
        disabled: block !== null,
        title: block ?? undefined,
        'aria-label': `Select ${row.company_name || 'Unnamed company'}`,
      };
    },
  };
}

function DeletionsTable({
  view,
  busyId = null,
  onAction = () => {},
  editScope = null,
  forceSelection,
  onRowsLoaded,
  onPageLoaded,
  onReadyChange,
  refetchInterval = false,
}: DeletionsTableProps) {
  const [page, setPage] = useState(1);
  const query = useCompanyDeletions(view, page, { refetchInterval });
  const data = query.data?.data;
  const text = VIEW_TEXT[view];

  useEffect(() => {
    if (!data) return;
    onRowsLoaded?.(data.rows);
    onPageLoaded?.(data);
  }, [data, onRowsLoaded, onPageLoaded]);

  const ready = query.isSuccess && !query.isFetching && !query.isError;
  useEffect(() => {
    onReadyChange?.(ready);
  }, [ready, onReadyChange]);

  // A Cancel or Retry can empty the page staff are on (its last row moves to
  // History). Step back to the last page that still has rows, so earlier
  // deletions never hide behind an empty page with no pager.
  const total = data?.total;
  const emptyPastFirst = page > 1 && data !== undefined && data.rows.length === 0;
  useEffect(() => {
    if (!emptyPastFirst || total === undefined) return;
    setPage(Math.max(1, Math.ceil(total / DELETIONS_PAGE_SIZE)));
  }, [emptyPastFirst, total]);

  const retryButton = (
    <Button size="small" onClick={() => void query.refetch()}>
      Retry
    </Button>
  );

  return (
    <section aria-label={text.title}>
      <Title level={5} style={{ marginTop: 0 }}>
        {text.title}
      </Title>
      {/* Loading, empty and failed are different: only a failed FIRST read hides the table. */}
      {query.isError && !data ? (
        <Alert type="error" showIcon message={text.failed} action={retryButton} />
      ) : (
        <>
          {query.isError ? (
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 8 }}
              message="Could not refresh, showing what was last loaded."
              action={retryButton}
            />
          ) : null}
          <Table<CompanyDeletionRow>
            rowKey="_id"
            size="small"
            loading={query.isLoading || query.isFetching}
            dataSource={data?.rows ?? []}
            rowSelection={forceRowSelection(data?.rows ?? [], forceSelection)}
            columns={
              view === 'active'
                ? activeDeletionColumns({ busyId, onAction, editScope })
                : historyDeletionColumns()
            }
            pagination={{
              current: page,
              pageSize: DELETIONS_PAGE_SIZE,
              total: data?.total ?? 0,
              showSizeChanger: false,
              hideOnSinglePage: true,
              onChange: setPage,
            }}
            locale={{
              emptyText: query.isLoading ? ' ' : <Text type="secondary">{text.empty}</Text>,
            }}
          />
        </>
      )}
    </section>
  );
}

export const CompanyDeletionsPage: React.FC = () => {
  const { data: permissions, isLoading: permissionsLoading } = usePermissions<string[]>();
  const queryClient = useQueryClient();
  const invalidate = useInvalidate();
  const action = useDeletionAction();
  const [busyId, setBusyId] = useState<string | null>(null);
  // SF-4668: Cancel and Retry only on companies the person may change. Fail
  // closed: while the list is being read again, or after a read failed, no
  // action is offered, even if an older answer is still cached (the person's
  // list may have changed since).
  const scopeQuery = useEditableCompanies();
  const { isLoading: scopeLoading, isFetching: scopeFetching, isError: scopeFailed } = scopeQuery;
  const scopeData = scopeQuery.data;
  // Memoised so the tick check below runs when the answer changes, not on every render.
  const editScope = useMemo(
    () =>
      settledEditScope({
        isLoading: scopeLoading,
        isFetching: scopeFetching,
        isError: scopeFailed,
        data: scopeData,
      }),
    [scopeLoading, scopeFetching, scopeFailed, scopeData],
  );
  // SF-5057: ticked deletions (by deletion id), kept across pages.
  const [forceTicked, setForceTicked] = useState<Map<string, CompanyDeletionRow>>(() => new Map());
  // The In progress page last loaded, and whether that read is settled and succeeded.
  const [activeRows, setActiveRows] = useState<CompanyDeletionRow[]>([]);
  const [activePage, setActivePage] = useState<DeletionsPageData | undefined>(undefined);
  const [activeListReady, setActiveListReady] = useState(false);
  // A ticked row that loads again forced by someone else, no longer waiting, or off the
  // person's list is unticked, so Delete now never offers it. While the list of companies is
  // being read again (null) nothing is offered anyway; the check runs once it is in.
  useEffect(() => {
    if (editScope === null) return;
    setForceTicked((prev) => reconcileForceTicks(prev, activeRows, editScope));
  }, [activeRows, editScope]);

  // While any deletion (on any page, per sf-api's count) is running or forced and waiting to
  // start, read both tables again every 30 seconds, so it moves to History without a reload.
  const inFlight = deletionsInFlight(activePage);
  const pollEvery = inFlight ? DELETIONS_POLL_MS : false;
  // The last one ending stops the polling: read History once more, since its own last poll
  // may have run just before the In progress read that saw the deletion end.
  const wasInFlight = useRef(false);
  useEffect(() => {
    if (wasInFlight.current && !inFlight) {
      void queryClient.invalidateQueries({ queryKey: [...companyDeletionsQueryKey, 'history'] });
    }
    wasInFlight.current = inFlight;
  }, [inFlight, queryClient]);

  if (permissionsLoading) return null;
  if (!hasPermission(permissions ?? [], COMPANY_DELETE_PERMISSION)) {
    return (
      <Result
        status="403"
        title="Not Authorized"
        subTitle="You do not have permission to see deleted companies."
      />
    );
  }

  // Both keys: admin:companies:delete is already required to see this page at all.
  const canForce = hasPermission(permissions ?? [], COMPANY_DELETE_FORCE_PERMISSION);
  const handleForceTicks = (keys: React.Key[], rows: (CompanyDeletionRow | undefined)[]) =>
    setForceTicked((prev) => {
      const byId = new Map(rows.filter(Boolean).map((r) => [r!._id, r!] as const));
      const next = new Map<string, CompanyDeletionRow>();
      keys.map(String).forEach((key) => {
        const row = byId.get(key) ?? prev.get(key);
        if (row) next.set(key, row);
      });
      return next;
    });
  const untickCompanies = (companyIds: string[]) =>
    setForceTicked(
      (prev) => new Map([...prev].filter(([, row]) => !companyIds.includes(row.company_id))),
    );

  const runAction = async (row: CompanyDeletionRow, kind: DeletionAction) => {
    if (busyId || !canEditCompanyId(editScope, row.company_id)) return;
    setBusyId(row._id);
    const args =
      kind === 'cancel' ? cancelDeletionArgs(row.company_id) : retryDeletionArgs(row.company_id);
    try {
      await action.mutateAsync(args);
      message.success(deletionActionDoneMessage(kind));
    } catch (error) {
      message.error(deletionActionErrorMessage(error, kind));
    } finally {
      setBusyId(null);
      // Refresh after a refusal too: a 409 means this list was out of date.
      void queryClient.invalidateQueries({ queryKey: companyDeletionsQueryKey });
      invalidate({ resource: 'companies', invalidates: ['list', 'detail'], id: row.company_id });
    }
  };

  return (
    <List resource="companies" title="Companies" breadcrumb={false} headerButtons={() => null}>
      <CompaniesTabs active="deleted" />
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        {scopeFailed ? (
          <Alert
            type="warning"
            showIcon
            message="Could not check which companies you can change, so Cancel and Retry are hidden."
            action={
              <Button size="small" onClick={() => void scopeQuery.refetch()}>
                Retry
              </Button>
            }
          />
        ) : null}
        {/* Outside the table's error branch: a failed reload must not lose a running batch. */}
        {canForce ? (
          <ForceDeleteBar
            selected={[...forceTicked.values()]}
            // Never delete from rows that are still loading or that failed to refresh.
            enabled={editScope !== null && activeListReady}
            onDone={untickCompanies}
            onClear={() => setForceTicked(new Map())}
          />
        ) : null}
        <DeletionsTable
          view="active"
          busyId={busyId}
          editScope={editScope}
          onAction={(r, k) => void runAction(r, k)}
          onRowsLoaded={setActiveRows}
          onPageLoaded={setActivePage}
          onReadyChange={setActiveListReady}
          refetchInterval={pollEvery}
          forceSelection={
            canForce && editScope
              ? { keys: [...forceTicked.keys()], onChange: handleForceTicks, editScope }
              : undefined
          }
        />
        <DeletionsTable view="history" refetchInterval={pollEvery} />
      </Space>
    </List>
  );
};
